import {
  LANGUAGES,
  SOURCES,
  MENTION_SOURCE_TYPES,
  SEARCH_TYPES_LIST,
  SearchType,
} from "@/constants";
import { useOrganization } from "@/hooks/useOrganization";
import { MinReachByChannelTypeSource } from "./reuestUtils";
import { UnsavedQuery } from "@/types/SearchQuery";
import { useLingui } from "@lingui/react/macro";
import z from "zod";

export type SearchIndexForm = {
  keywords: string[];
  nearKeywords: Nullable<string[]>;
  notNearKeywords: Nullable<string[]>;
};

export function useSearchIndexFormSchema() {
  const { t } = useLingui();
  const { data: orgData } = useOrganization();
  const maxNumKeywordsPerQuery = orgData?.ok
    ? (orgData.data?.plan?.max_num_keywords_per_query ?? 0)
    : 0;

  let keywords = z
    .array(z.string())
    .min(1, { message: t`Vänligen ange minst 1 sökord` });

  if (maxNumKeywordsPerQuery > 0) {
    keywords = keywords.max(maxNumKeywordsPerQuery, {
      message: t`Trial-konton är begränsade till ${maxNumKeywordsPerQuery} söktermer per söksats, kontakta oss för att utöka.`,
    });
  }

  const queryBuilderFormSchema = z.object({
    keywords: keywords,
    nearKeywords: z.array(z.string()).nullable(),
    notNearKeywords: z.array(z.string()).nullable(),
  });

  return queryBuilderFormSchema;
}

export type SearchPreviewFormType = {
  start_date: Date;
  end_date: Nullable<Date>;
  queries: UnsavedQuery[];
  information_need: Nullable<string>;
};

export enum PreviewFormSubmitButton {
  Preview = "preview-form-preview",
  Create = "preview-form-save",
  Follow = "preview-form-follow",
}

export function useSearchPreviewFormSchema(
  minReachByChannelTypeSources: MinReachByChannelTypeSource[]
) {
  const { t } = useLingui();
  const { data: orgData } = useOrganization();
  const maxNumKeywordsPerQuery = orgData?.ok
    ? (orgData.data?.plan?.max_num_keywords_per_query ?? 0)
    : 0;

  let keywords = z
    .array(z.string())
    .min(1, { message: t`Vänligen ange minst 1 sökord` });

  if (maxNumKeywordsPerQuery > 0) {
    keywords = keywords.max(maxNumKeywordsPerQuery, {
      message: t`Trial-konton är begränsade till ${maxNumKeywordsPerQuery} söktermer per söksats, kontakta oss för att utöka.`,
    });
  }

  const minReachShape = {
    ...Object.fromEntries(
      minReachByChannelTypeSources.map((source) => [source, z.number()])
    ),
    has_custom_values: z.boolean(),
  } as Record<MinReachByChannelTypeSource, z.ZodNumber> & {
    has_custom_values: z.ZodBoolean;
  };

  const querySchema = z.object({
    keywords,
    languages: z.array(z.enum(LANGUAGES)).nullable(),
    sources: z.array(z.enum(SOURCES)).nullable(),
    tags: z.array(z.string()).nullable(),
    near_keywords: z.array(z.string()).nullable(),
    near_distance: z
      .number()
      .positive({
        message: t`Avståndet måste vara positivt`,
      })
      .nullable(),
    near_near_keywords: z.array(z.string()).nullable(),
    not_near_keywords: z.array(z.string()).nullable(),
    not_near_distance: z
      .number()
      .positive({
        message: t`Avståndet måste vara positivt`,
      })
      .nullable(),
    include_channel_names: z.array(z.string()).nullable(),
    exclude_channel_names: z.array(z.string()).nullable(),
    min_reach_by_channel_type: z.object(minReachShape).nullable(),
    mention_types: z.enum(MENTION_SOURCE_TYPES).nullable(),
    include_paywalled: z.boolean().nullable(),
  });

  const queryBuilderFormSchema = z
    .object({
      start_date: z.date(),
      end_date: z.date().nullable(),
      queries: z.array(querySchema),
      information_need: z.string().nullable(),
    })
    .superRefine((data, ctx) => {
      if (data.end_date && data.start_date > data.end_date) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t`Startdatum får inte vara efter slutdatum`,
          path: ["start_date"],
        });
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t`Slutdatum får inte vara före startdatum`,
          path: ["end_date"],
        });
      }
    });

  return queryBuilderFormSchema;
}

export type SearchSubmitForm = {
  title: string;
  type: SearchType;
  start_date: Date;
  end_date: Nullable<Date>;
};

export function useSearchSubmitFormSchema() {
  const { t } = useLingui();

  const searchSubmitFormSchema = z.object({
    title: z.string().min(1, {
      message: t`Vänligen ge din bevakning en titel`,
    }),
    type: z.enum(SEARCH_TYPES_LIST, {
      message: t`Vänligen ange vad du bevakar`,
    }),
    start_date: z.date(),
    end_date: z.date().nullable(),
  });

  return searchSubmitFormSchema;
}
